import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import useFeaturePermission from '../hooks/useFeaturePermission';
import { useSelector } from '../redux/store';
import { LoadingScreen } from '../components/loading';
import { DASHBOARD_PATH } from '../utils/practiceSwitchRoute';

export default function FeatureGuard({ feature, level = 'view', children, fallback }) {
    const { canView, canManage } = useFeaturePermission(feature);
    const permissionsLoading = useSelector((state) => state.permissions.loading);
    const permissions = useSelector((state) => state.permissions.permissions);
    const pendingSwitchLanding = useSelector((state) => state.practiceTreatmentList?.pendingSwitchLanding);
    const { pathname } = useLocation();

    // Wait for permissions to load before making access decisions
    const permissionsLoaded = !permissionsLoading && Object.keys(permissions).length > 0;
    if (!permissionsLoaded) {
        return <LoadingScreen />;
    }

    const hasAccess = level === 'manage' ? canManage : canView;

    if (!hasAccess) {
        if (fallback === 'redirect') {
            // A practice switch just landed the operator on this exact path and
            // the incoming practice does not have the feature. That is a normal
            // consequence of switching, not an access violation, so send them to
            // the dashboard instead of a Permission Denied dead end. Deciding it
            // here — rather than only in the header's post-switch reconciliation
            // — matters because <Navigate> fires from an effect: whichever of the
            // two runs last wins, so both have to agree on the destination.
            const landedFromPracticeSwitch = Boolean(pendingSwitchLanding)
                && pendingSwitchLanding.pathname === pathname;
            return <Navigate to={landedFromPracticeSwitch ? DASHBOARD_PATH : '/dashboard/permission-denied'} replace />;
        }
        return fallback || null;
    }

    return <>{children}</>;
}

FeatureGuard.propTypes = {
    feature: PropTypes.string.isRequired,
    level: PropTypes.oneOf(['view', 'manage']),
    children: PropTypes.node.isRequired,
    fallback: PropTypes.node,
};
